// components/BookAppointment.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import PaymentStep from './PaymentStep';
import TermsPrivacyModal from './TermsPrivacyModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  category: string;
  requires_deposit: boolean;
  deposit_amount: number;
  requires_consultation: boolean;
}

interface Professional {
  id: number;
  nombre: string;
  apellido: string;
  business_name: string;
  especialidad: string;
  professional_type: string;
  picture: string;
  telefono?: string;
  services: Service[];
  booking_extra_fields?: {
    enabled: boolean;
    fields: Array<{
      key: string;
      label: string;
      type: string;
      required: boolean;
      predefined: boolean;
    }>;
  };
}

interface TimeSlot {
  time: string;
  available: boolean;
  datetime: string;
}

interface BookingData {
  professionalId: number;
  serviceId: number;
  appointmentDate: string;
  appointmentTime: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientNotes: string;
  isFirstTime: boolean;
  urgencyLevel: string;
  locationId?: number;
}

interface LocationPublic {
  id: number;
  name: string;
  address: string;
  city: string;
  phone: string;
}
// =====================================================
// MODAL DE SUGERENCIA DE REGISTRO
// =====================================================
interface RegistrationSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: () => void;
  clientName: string;
  appointmentCount: number;
}

const RegistrationSuggestionModal: React.FC<RegistrationSuggestionModalProps> = ({
  isOpen,
  onClose,
  onRegister,
  clientName,
  appointmentCount
}) => {
  if (!isOpen) return null;

  const benefits = [
    {
      icon: '📅',
      title: 'Gestiona tus turnos',
      description: 'Ve todos tus turnos en un solo lugar'
    },
    {
      icon: '🔔',
      title: 'Recordatorios automáticos',
      description: 'Nunca más olvides una cita'
    },
    {
      icon: '📊',
      title: 'Historial completo',
      description: 'Accede a tu historial médico/de tratamientos'
    },
    {
      icon: '⚡',
      title: 'Reserva más rápido',
      description: 'Tus datos ya estarán guardados'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 transform animate-in zoom-in-95 duration-200">
        {/* Header con animación */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-4xl">🎉</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Hola {clientName}! 👋
          </h2>
          <p className="text-gray-600">
            {appointmentCount === 1 
              ? 'Vemos que es tu segunda cita con nosotros' 
              : `Ya has agendado ${appointmentCount + 1} citas con nosotros`}
          </p>
        </div>

        {/* Beneficios */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            ¿Sabías que puedes crear una cuenta y obtener estos beneficios?
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-2xl">{benefit.icon}</span>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botones */}
        <div className="space-y-3">
          <button
            onClick={onRegister}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            ✨ Crear mi cuenta gratis
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Tal vez más tarde
          </button>
        </div>

        {/* Nota pequeña */}
        <p className="text-center text-xs text-gray-500 mt-4">
          Tus datos están seguros. Solo usaremos tu información para mejorar tu experiencia.
        </p>
      </div>
    </div>
  );
};
const BookAppointment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const preSelectedServiceId = searchParams.get('service');
  
  // Estados
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // NUEVOS ESTADOS:
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [previousAppointments, setPreviousAppointments] = useState(0);
  
  // Estados de reserva
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // ✅ Multi-sede
  const [locations, setLocations] = useState<LocationPublic[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationPublic | null>(null);

  // ✅ Calendario vista mes
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [specialDays, setSpecialDays] = useState<string[]>([]);
  const [maxAdvanceBooking, setMaxAdvanceBooking] = useState<number>(30);
  
  // Datos del cliente
  const [bookingData, setBookingData] = useState<Partial<BookingData>>({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientNotes: '',
    isFirstTime: false,
    urgencyLevel: 'normal'
  });

  // Estados para campos extra del booking
  const [patientExtraData, setPatientExtraData] = useState<Record<string, string>>({});

  // Consentimiento
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms');

  // Estados para pago
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState<string | null>(null);
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [finalPaymentInfo, setFinalPaymentInfo] = useState<{
    amount: number;
    tipoCobro: string;
    porcentajeSena?: number;
    method?: string;
  } | null>(null);

  // Cargar profesional y preseleccionar servicio si existe
  useEffect(() => {
    if (id) {
      fetchProfessional(id);
    }
  }, [id]);

  // Preseleccionar servicio si viene en la URL
  useEffect(() => {
    if (professional && preSelectedServiceId) {
      const service = professional.services.find(s => s.id === parseInt(preSelectedServiceId));
      if (service) {
        setSelectedService(service);
        setCurrentStep(2);
      }
    }
  }, [professional, preSelectedServiceId]);

  // Cargar horarios disponibles cuando se selecciona una fecha (y sede si hay múltiples)
  useEffect(() => {
    if (selectedDate && selectedService && id) {
      // Si hay sedes, necesitamos una seleccionada; si no hay, cargar sin filtro
      if (locations.length > 1 && !selectedLocation) return;
      fetchAvailableSlots(id, selectedDate, selectedService.id, selectedLocation?.id);
    }
  }, [selectedDate, selectedService, id, selectedLocation]);

  const fetchProfessional = async (professionalId: string) => {
    setIsLoading(true);
    try {
      // Usar endpoints públicos (no requieren auth)
      const [profResponse, servicesResponse] = await Promise.all([
        fetch(`${API_URL}/api/professionals/${professionalId}/public`),
        fetch(`${API_URL}/api/professionals/${professionalId}/services/public`)
      ]);
      if (!profResponse.ok) throw new Error('Profesional no encontrado');

      const profData = await profResponse.json();
      const servicesData = servicesResponse.ok ? await servicesResponse.json() : { services: [] };

      const prof = profData.professional;
      prof.services = servicesData.services || [];
      setProfessional(prof);

      // ✅ Cargar sedes públicas
      try {
        const locResponse = await fetch(`${API_URL}/api/locations/professional/${professionalId}/public`);
        if (locResponse.ok) {
          const locData = await locResponse.json();
          const locs = locData.locations || [];
          setLocations(locs);
          // Si hay solo una sede, auto-seleccionarla y cargar su schedule
          if (locs.length === 1) {
            setSelectedLocation(locs[0]);
            fetchScheduleForLocation(professionalId, locs[0].id);
          } else if (locs.length === 0) {
            // Sin sedes, cargar schedule genérico
            fetchScheduleForLocation(professionalId);
          }
        }
      } catch (locErr) {
        console.error('Error fetching locations:', locErr);
      }
    } catch (error) {
      console.error('Error fetching professional:', error);
      setError('Error al cargar los datos del profesional');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableSlots = async (professionalId: string, date: string, serviceId: number, locationId?: number) => {
    setLoadingSlots(true);
    try {
      let url = `${API_URL}/api/appointments/professional/${professionalId}/available-slots?date=${date}&serviceId=${serviceId}`;
      if (locationId) url += `&locationId=${locationId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Error al cargar horarios');
      
      const data = await response.json();
      setAvailableSlots(data.availableSlots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setSelectedDate('');
    setSelectedTime('');
    // Si hay solo una sede, ya está seleccionada; si hay múltiples, resetear selección
    if (locations.length <= 1) {
      setCurrentStep(2);
    } else {
      // Si no hay sede seleccionada, ir a paso 2 que mostrará el selector
      setCurrentStep(2);
    }
  };

  const handleLocationSelect = (location: LocationPublic) => {
    setSelectedLocation(location);
    setSelectedDate('');
    setSelectedTime('');
    // Cargar schedule de esta sede para el calendario
    if (id) fetchScheduleForLocation(id, location.id);
  };

  const fetchScheduleForLocation = async (professionalId: string, locationId?: number) => {
    try {
      const locQuery = locationId ? `?locationId=${locationId}` : '';
      // Cargar schedule + excepciones en paralelo
      const [schedRes, excRes] = await Promise.all([
        fetch(`${API_URL}/api/schedule/professional/${professionalId}/config/public${locQuery}`),
        fetch(`${API_URL}/api/schedule/professional/${professionalId}/exceptions/public${locQuery}`)
      ]);
      if (schedRes.ok) {
        const data = await schedRes.json();
        if (data.success && data.scheduleConfig?.workingDays) {
          setProfessionalSchedule(data.scheduleConfig.workingDays);
          if (data.scheduleConfig.maxAdvanceBooking) {
            setMaxAdvanceBooking(data.scheduleConfig.maxAdvanceBooking);
          }
        }
      }
      if (excRes.ok) {
        const excData = await excRes.json();
        if (excData.success && excData.exceptions) {
          const blocked = excData.exceptions
            .filter((e: any) => !e.is_working)
            .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : '');
          setBlockedDates(blocked);
          const special = excData.exceptions
            .filter((e: any) => e.is_working)
            .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : '');
          setSpecialDays(special);
        }
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setCurrentStep(3);
  };

  // Verificar turnos previos del email
  const checkPreviousAppointments = async (email: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/appointments/check-previous?email=${encodeURIComponent(email)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Turnos previos:', data);
        
        setPreviousAppointments(data.appointmentCount);
        
        // Si debe sugerir registro, retornar true
        if (data.shouldSuggestRegistration) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error verificando turnos previos:', error);
      return false;
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedService || !selectedDate || !selectedTime || !professional) return;

    // Verificar si necesita mostrar paso de pago
    try {
      const paymentConfigResponse = await fetch(`${API_URL}/api/payments/config/${id}`);
      if (paymentConfigResponse.ok) {
        const paymentConfigData = await paymentConfigResponse.json();

        if (paymentConfigData.success) {
          const config = paymentConfigData.data;
          setPaymentConfig(config);

          const requiresPayment = config.tipo_cobro === 'solo_sena' || config.tipo_cobro === 'consulta_completa';

          if (requiresPayment) {
            // Calcular monto a cobrar
            let amountToPay = selectedService.price;

            if (config.tipo_cobro === 'solo_sena' && config.porcentaje_sena) {
              amountToPay = Math.round(selectedService.price * (config.porcentaje_sena / 100));
            }

            setFinalPaymentInfo({
              amount: amountToPay,
              tipoCobro: config.tipo_cobro,
              porcentajeSena: config.porcentaje_sena
            });

            // Guardar datos en sessionStorage para PaymentStep
            const bookingDataForPayment = {
              serviceId: selectedService.id,
              appointmentDate: selectedDate,
              appointmentTime: selectedTime,
              clientName: (bookingData.clientName || '').trim(),
              clientEmail: (bookingData.clientEmail || '').trim() || null,
              clientPhone: (bookingData.clientPhone || '').trim(),
              notes: (bookingData.clientNotes || '').trim() || null,
              isFirstTime: bookingData.isFirstTime,
              urgencyLevel: bookingData.urgencyLevel,
              price: amountToPay,
              fullPrice: selectedService.price,
              duration: selectedService.duration,
              depositRequired: config.tipo_cobro === 'solo_sena',
              depositAmount: config.tipo_cobro === 'solo_sena' ? amountToPay : 0,
              serviceName: selectedService.name,
              professionalName: professional.business_name || `${professional.nombre} ${professional.apellido}`,
              professionalId: professional.id,
              tipoCobro: config.tipo_cobro,
              porcentajeSena: config.porcentaje_sena,
              locationId: selectedLocation?.id || null
            };

            sessionStorage.setItem('pendingBooking', JSON.stringify(bookingDataForPayment));

            setShowPaymentStep(true);
            return;
          }

          // Efectivo: guardar info para confirmación
          setFinalPaymentInfo({
            amount: selectedService.price,
            tipoCobro: config.tipo_cobro,
            method: 'efectivo'
          });
        }
      }
    } catch (error) {
      console.error('Error verificando config de pagos:', error);
    }

    // Si no requiere pago, continuar con la reserva
    await submitAppointment();
  };

  const submitAppointment = async (method?: string, proofUrl?: string) => {
    setIsSubmitting(true);
    setError('');

    // Cargar datos de pago del sessionStorage si existen
    let bookingDataFromSession: any = null;
    let finalDepositAmount = 0;
    let finalDepositRequired = false;

    try {
      const pendingBooking = sessionStorage.getItem('pendingBooking');
      if (pendingBooking) {
        bookingDataFromSession = JSON.parse(pendingBooking);
        finalDepositAmount = bookingDataFromSession.depositAmount || bookingDataFromSession.price;
        finalDepositRequired = bookingDataFromSession.depositRequired;
      }
    } catch (e) {
      console.error('Error al parsear sessionStorage:', e);
    }

    try {
      // 1. Verificar si debe sugerir registro
      const shouldSuggest = await checkPreviousAppointments(bookingData.clientEmail || '');

      const priceForAppointment = bookingDataFromSession?.fullPrice || selectedService!.price;

      // 2. Crear la cita
      const appointmentData = {
        serviceId: selectedService!.id,
        appointmentDate: selectedDate,
        appointmentTime: selectedTime,
        clientName: bookingData.clientName,
        clientEmail: bookingData.clientEmail,
        clientPhone: bookingData.clientPhone,
        clientNotes: bookingData.clientNotes,
        isFirstTime: bookingData.isFirstTime,
        urgencyLevel: bookingData.urgencyLevel,
        price: priceForAppointment,
        duration: selectedService!.duration,
        depositRequired: finalDepositRequired,
        depositAmount: finalDepositAmount,
        metodo_pago_elegido: method || null,
        comprobante_pago_url: proofUrl || null,
        pago_verificado: method === 'mercadopago',
        locationId: selectedLocation?.id || null,
        patient_extra_data: Object.keys(patientExtraData).length > 0 ? patientExtraData : null
      };

      const response = await fetch(`${API_URL}/api/appointments/professional/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear la cita');
      }

      const result = await response.json();

      // Limpiar sessionStorage
      sessionStorage.removeItem('pendingBooking');

      // Guardar método de pago para el paso 4
      if (method) {
        setPaymentMethod(method);
        setFinalPaymentInfo(prev => prev ? { ...prev, method } : { amount: selectedService!.price, tipoCobro: 'efectivo', method });
      }

      // 3. Éxito - decidir qué mostrar
      if (shouldSuggest) {
        setShowRegistrationModal(true);
      } else {
        setCurrentStep(4);
      }

    } catch (error: any) {
      console.error('Error booking appointment:', error);
      setError(error.message || 'Error al agendar la cita');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentComplete = (method: string, proofUrl?: string) => {
    setPaymentMethod(method);
    setPaymentProofUrl(proofUrl || null);
    setShowPaymentStep(false);
    
    // Ahora sí, enviar la cita con los datos de pago
    submitAppointment(method, proofUrl);
  };

  const handlePaymentSkip = () => {
    setShowPaymentStep(false);
    // Continuar sin pago
    submitAppointment();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  };

  // Manejar registro desde el modal
  const handleRegisterFromModal = () => {
    setShowRegistrationModal(false);
    // Pre-llenar datos de registro y navegar
    navigate('/register', {
      state: {
        prefilledData: {
          nombre: bookingData.clientName?.split(' ')[0] || '',
          apellido: bookingData.clientName?.split(' ').slice(1).join(' ') || '',
          email: bookingData.clientEmail,
          telefono: bookingData.clientPhone
        }
      }
    });
  };

  const handleCloseRegistrationModal = () => {
    setShowRegistrationModal(false);
    // Ir a confirmación normal
    setCurrentStep(4);
  };

  // ✅ Calendario vista mes
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNamesShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const isDateAvailableForBooking = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    if (specialDays.includes(dateStr)) return true;
    const dayOfWeek = date.getDay();
    const dayMapping: Record<string, number> = {
      'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
      'thursday': 4, 'friday': 5, 'saturday': 6
    };
    const daySchedule = professionalSchedule.find(d => dayMapping[d.day] === dayOfWeek);
    return daySchedule ? daySchedule.isWorking : false;
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isBeyondMaxAdvance = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxAdvanceBooking);
    return date > maxDate;
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    const days = [];
    const current = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      const date = new Date(current);
      const isCurrentMonth = date.getMonth() === month;
      const isPast = isPastDate(date);
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const isBlocked = isCurrentMonth && !isPast && blockedDates.includes(dateStr);
      const isTooFar = isBeyondMaxAdvance(date);
      const isAvailable = isCurrentMonth && !isPast && !isTooFar && !isBlocked && isDateAvailableForBooking(date);
      const isSelected = dateStr === selectedDate;
      const isToday = date.toDateString() === new Date().toDateString();
      days.push({ date, day: date.getDate(), isCurrentMonth, isAvailable, isPast, isBlocked, isSelected, isToday, dateStr });
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (error && !professional) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error al cargar</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/search" className="text-blue-600 hover:text-blue-800">
            Volver a la búsqueda
          </Link>
        </div>
      </div>
    );
  }

  const displayName = professional?.business_name || `${professional?.nombre} ${professional?.apellido}`;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4 sm:mb-8">
          <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 flex-wrap">
            <Link to="/search" className="hover:text-blue-600">Buscar profesionales</Link>
            <span>/</span>
            <Link to={`/p/${id}`} className="hover:text-blue-600 truncate max-w-[150px] sm:max-w-none">{displayName}</Link>
            <span>/</span>
            <span className="text-gray-900">Agendar Cita</span>
          </div>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border-t-4 border-t-[#0891b2]">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gradient-to-br from-[#0891b2] to-[#10b981] flex-shrink-0">
              {professional?.picture ? (
                <img src={professional.picture} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg sm:text-xl">
                  {professional?.nombre?.charAt(0)}{professional?.apellido?.charAt(0)}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{displayName}</h1>
              <p className="text-blue-600 font-medium text-sm sm:text-base">{professional?.especialidad}</p>
            </div>
          </div>
        </div>

        {/* Progress Steps - responsive con colores del logo */}
        <div className="mb-6 sm:mb-8">
          {/* Barra de progreso visual */}
          <div className="relative mb-4">
            <div className="h-1.5 bg-gray-200 rounded-full">
              <div
                className="h-1.5 rounded-full bg-gradient-to-r from-[#0891b2] to-[#10b981] transition-all duration-500"
                style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            {[
              { step: 1, label: 'Servicio', mobileLabel: 'Servicio' },
              { step: 2, label: 'Fecha y Hora', mobileLabel: 'Fecha' },
              { step: 3, label: 'Datos', mobileLabel: 'Datos' },
              { step: 4, label: 'Confirmación', mobileLabel: 'Confirmar' }
            ].map(({ step, label, mobileLabel }) => (
              <div key={step} className="flex flex-col items-center">
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300 ${
                  currentStep > step
                    ? 'bg-gradient-to-r from-[#0891b2] to-[#10b981] text-white shadow-md'
                    : currentStep === step
                      ? 'bg-gradient-to-r from-[#0891b2] to-[#10b981] text-white shadow-lg scale-110'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step ? '✓' : step}
                </div>
                <span className={`mt-1 text-[10px] sm:text-xs font-medium text-center ${
                  currentStep >= step ? 'text-[#0891b2]' : 'text-gray-400'
                }`}>
                  <span className="sm:hidden">{mobileLabel}</span>
                  <span className="hidden sm:inline">{label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
          {/* Paso 1: Seleccionar Servicio */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">🩺 Selecciona un servicio</h2>
              <div className="grid gap-4">
                {professional?.services?.map(service => (
                  <div
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className="border border-gray-200 rounded-xl p-4 hover:border-[#0891b2] hover:bg-cyan-50 cursor-pointer transition-all duration-200 hover:shadow-md"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                        {service.description && (
                          <p className="text-gray-600 text-sm mb-2">{service.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{formatDuration(service.duration)}</span>
                          {service.requires_consultation && (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                              Requiere consulta
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-gray-900">
                          {formatPrice(service.price)}
                        </div>
                        {service.requires_deposit && (
                          <div className="text-sm text-blue-600">
                            Seña: {formatPrice(service.deposit_amount)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Paso 2: Seleccionar Sede (si hay múltiples), Fecha y Hora */}
          {currentStep === 2 && selectedService && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                📅 Fecha y hora para: <span className="text-[#0891b2]">{selectedService.name}</span>
              </h2>

              {/* ✅ Selector de sede (solo si hay más de 1) */}
              {locations.length > 1 && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Selecciona una sede</h3>
                  <div className="grid gap-3">
                    {locations.map(loc => (
                      <div
                        key={loc.id}
                        onClick={() => handleLocationSelect(loc)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          selectedLocation?.id === loc.id
                            ? 'border-[#0891b2] bg-cyan-50 text-[#0891b2] shadow-md'
                            : 'border-gray-200 hover:border-[#10b981] hover:shadow-sm'
                        }`}
                      >
                        <div className="font-semibold">{loc.name}</div>
                        {loc.city && <div className="text-sm text-gray-500">{loc.city}</div>}
                        {loc.address && <div className="text-sm text-gray-400">{loc.address}</div>}
                        {loc.phone && <div className="text-sm text-green-600 mt-1">📞 {loc.phone}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mostrar selector de fecha solo si hay sede seleccionada (o si hay una sola sede) */}
              {(locations.length <= 1 || selectedLocation) && (
              <>
              {/* Calendario vista mes */}
              <div className="mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-1 hover:bg-gray-100 rounded">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                    {(() => {
                      const maxDate = new Date();
                      maxDate.setDate(maxDate.getDate() + maxAdvanceBooking);
                      const canGoNext = currentMonth.getFullYear() < maxDate.getFullYear() ||
                        (currentMonth.getFullYear() === maxDate.getFullYear() && currentMonth.getMonth() < maxDate.getMonth());
                      return (
                        <button type="button"
                          onClick={() => canGoNext && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                          className={`p-1 rounded ${canGoNext ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
                          disabled={!canGoNext}
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                      );
                    })()}
                  </div>
                  {/* Nombres de días */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNamesShort.map(d => <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>)}
                  </div>
                  {/* Días */}
                  <div className="grid grid-cols-7 gap-1">
                    {generateCalendarDays().map((dayInfo, idx) => {
                      let cls = "w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-xs sm:text-sm rounded-md transition-all duration-200 ";
                      if (!dayInfo.isCurrentMonth) cls += "text-gray-300 cursor-not-allowed ";
                      else if (dayInfo.isPast) cls += "text-gray-400 cursor-not-allowed bg-gray-50 ";
                      else if (dayInfo.isBlocked) cls += "text-red-500 bg-red-100 cursor-not-allowed line-through font-medium ";
                      else if (dayInfo.isAvailable) {
                        if (dayInfo.isSelected) cls += "bg-blue-600 text-white font-semibold shadow-md cursor-pointer ";
                        else cls += "bg-green-100 text-green-800 hover:bg-green-200 font-medium cursor-pointer ";
                      } else cls += "text-gray-400 bg-red-50 cursor-not-allowed ";
                      if (dayInfo.isToday && !dayInfo.isSelected) cls += "ring-2 ring-blue-300 ";
                      return (
                        <button key={idx} type="button" disabled={!dayInfo.isAvailable || !dayInfo.isCurrentMonth}
                          onClick={() => dayInfo.isAvailable && handleDateSelect(dayInfo.dateStr)}
                          className={cls}>{dayInfo.day}</button>
                      );
                    })}
                  </div>
                  {/* Leyenda */}
                  <div className="mt-4 flex items-center justify-center space-x-4 text-xs flex-wrap gap-y-1">
                    <div className="flex items-center"><div className="w-3 h-3 bg-green-100 rounded mr-1"></div><span className="text-gray-600">Disponible</span></div>
                    <div className="flex items-center"><div className="w-3 h-3 bg-red-50 rounded mr-1"></div><span className="text-gray-600">No disponible</span></div>
                    {blockedDates.length > 0 && <div className="flex items-center"><div className="w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></div><span className="text-gray-600">Bloqueado</span></div>}
                    <div className="flex items-center"><div className="w-3 h-3 bg-blue-600 rounded mr-1"></div><span className="text-gray-600">Seleccionado</span></div>
                  </div>
                </div>
              </div>

              {/* Selector de hora */}
              {selectedDate && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Horarios disponibles</h3>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
                      {availableSlots.map(slot => (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && handleTimeSelect(slot.time)}
                          disabled={!slot.available}
                          className={`p-2.5 sm:p-3 rounded-xl text-center text-sm sm:text-base transition-all duration-200 ${
                            selectedTime === slot.time
                              ? 'bg-gradient-to-r from-[#0891b2] to-[#10b981] text-white shadow-md font-medium'
                              : slot.available
                                ? 'border border-gray-200 hover:border-[#0891b2] hover:bg-cyan-50 font-medium'
                                : 'border border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No hay horarios disponibles para esta fecha
                    </div>
                  )}
                </div>
              )}

              {/* Botones de navegación */}
              <div className="flex justify-between mt-6 sm:mt-8">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-4 sm:px-6 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm sm:text-base transition-colors"
                >
                  ← Cambiar servicio
                </button>
                {selectedTime && (
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-[#0891b2] to-[#10b981] text-white rounded-xl hover:shadow-lg text-sm sm:text-base font-medium transition-all duration-200"
                  >
                    Continuar →
                  </button>
                )}
              </div>
              </>
              )}
            </div>
          )}

          {/* Paso 3: Datos del Cliente */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Tus datos</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingData.clientName || ''}
                      onChange={(e) => handleInputChange('clientName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-gray-400 text-xs">(opcional)</span>
                    </label>
                    <input
                      type="email"
                      value={bookingData.clientEmail || ''}
                      onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Si no tiene, dejar vacío"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      required
                      value={bookingData.clientPhone || ''}
                      onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urgencia
                    </label>
                    <select
                      value={bookingData.urgencyLevel || 'normal'}
                      onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="low">Baja</option>
                      <option value="normal">Normal</option>
                      <option value="high">Urgente</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas adicionales
                  </label>
                  <textarea
                    rows={3}
                    value={bookingData.clientNotes || ''}
                    onChange={(e) => handleInputChange('clientNotes', e.target.value)}
                    placeholder="Comentarios, síntomas, preferencias..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isFirstTime"
                    checked={bookingData.isFirstTime || false}
                    onChange={(e) => handleInputChange('isFirstTime', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isFirstTime" className="ml-2 text-sm text-gray-700">
                    Es mi primera vez con este profesional
                  </label>
                </div>

                {/* Campos extra dinámicos del profesional */}
                {professional?.booking_extra_fields?.enabled && professional.booking_extra_fields.fields?.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Información adicional solicitada por el profesional</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {professional.booking_extra_fields.fields.map((field: any) => (
                        <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.type === 'textarea' ? (
                            <textarea
                              rows={2}
                              required={field.required}
                              value={patientExtraData[field.key] || ''}
                              onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          ) : field.type === 'date' ? (
                            <input
                              type="date"
                              required={field.required}
                              value={patientExtraData[field.key] || ''}
                              onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          ) : (
                            <input
                              type="text"
                              required={field.required}
                              value={patientExtraData[field.key] || ''}
                              onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Consentimiento de datos */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-600">
                      Acepto los{' '}
                      <button
                        type="button"
                        onClick={() => { setTermsModalTab('terms'); setShowTermsModal(true); }}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        términos y condiciones
                      </button>{' '}
                      y la{' '}
                      <button
                        type="button"
                        onClick={() => { setTermsModalTab('privacy'); setShowTermsModal(true); }}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        política de privacidad
                      </button>
                      . Autorizo el uso de mis datos con fines administrativos y asistenciales.
                    </label>
                  </div>
                  {!acceptedTerms && error && (
                    <p className="text-red-500 text-xs mt-1 ml-7">Debes aceptar los términos para continuar</p>
                  )}
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Volver
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting || !acceptedTerms}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Agendando...' : 'Confirmar Cita'}
                  </button>
                </div>

                <TermsPrivacyModal
                  isOpen={showTermsModal}
                  onClose={() => setShowTermsModal(false)}
                  initialTab={termsModalTab}
                />
              </form>
            </div>
          )}

          {/* Paso 4: Confirmación */}
          {currentStep === 4 && (
            <div className="text-center py-8">
              {/* Icono y título dinámico según método de pago */}
              {paymentMethod === 'transferencia' ? (
                <>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Turno Pendiente!</h2>
                  <p className="text-gray-600 mb-4">Tu turno será confirmado cuando verifiquemos el pago</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Cita Agendada!</h2>
                  <p className="text-gray-600 mb-4">Tu consulta ha sido programada exitosamente</p>
                </>
              )}

              {/* Aviso especial para transferencias */}
              {paymentMethod === 'transferencia' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800 mb-1">Pago por transferencia</h4>
                      <p className="text-xs text-amber-700">
                        El turno se confirmará cuando el profesional verifique tu comprobante. Recibirás una confirmación por WhatsApp o email.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
                <h3 className="font-semibold text-gray-900 mb-3">Detalles de tu cita:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Profesional:</span> <span className="font-medium">{displayName}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Servicio:</span> <span className="font-medium">{selectedService?.name}</span></div>
                  {selectedLocation && (
                    <div className="flex justify-between"><span className="text-gray-600">Sede:</span> <span className="font-medium">{selectedLocation.name}{selectedLocation.city ? ` — ${selectedLocation.city}` : ''}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-600">Fecha:</span> <span className="font-medium">{selectedDate.split('-').reverse().join('/')}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Hora:</span> <span className="font-bold text-blue-600">{selectedTime}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Duración:</span> <span className="font-medium">{selectedService && formatDuration(selectedService.duration)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Precio Total:</span> <span className="font-bold text-green-600">{selectedService && formatPrice(selectedService.price)}</span></div>

                  {/* Sección de pago */}
                  {finalPaymentInfo && finalPaymentInfo.tipoCobro !== 'efectivo' && (
                    <div className="pt-2 border-t">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {finalPaymentInfo.tipoCobro === 'solo_sena' ? 'Seña Pagada:' : 'Monto Pagado:'}
                        </span>
                        <span className="font-bold text-orange-600">
                          {formatPrice(finalPaymentInfo.amount)}
                          {finalPaymentInfo.porcentajeSena && finalPaymentInfo.tipoCobro === 'solo_sena' && (
                            ` (${finalPaymentInfo.porcentajeSena}%)`
                          )}
                        </span>
                      </div>
                      {finalPaymentInfo.tipoCobro === 'solo_sena' && selectedService && (
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">Saldo pendiente:</span>
                          <span className="text-xs text-gray-500">
                            {formatPrice(selectedService.price - finalPaymentInfo.amount)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {(!finalPaymentInfo || finalPaymentInfo.tipoCobro === 'efectivo') && !paymentMethod && (
                    <div className="pt-2 border-t">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Método de pago:</span>
                        <span className="font-bold text-gray-700">Abonás en consultorio (efectivo o transferencia)</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Info de contacto */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6 max-w-md mx-auto text-left">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">Te contactaremos pronto</h4>
                    <p className="text-xs text-blue-800">
                      Recibirás la confirmación por WhatsApp o email. Si tienes alguna pregunta, puedes contactar directamente al profesional.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {(selectedLocation?.phone || professional?.telefono) && (
                  <a
                    href={`https://wa.me/${(selectedLocation?.phone || professional?.telefono || '').replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.34 0-4.508-.804-6.222-2.15l-.435-.348-3.2 1.073 1.073-3.2-.348-.435A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                    Contactar por WhatsApp{selectedLocation?.phone ? ` (${selectedLocation.name})` : ''}
                  </a>
                )}
                <Link
                  to={`/p/${professional?.id || id}`}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-center font-medium"
                >
                  Agendar otra cita
                </Link>
              </div>
            </div>
          )}
        </div>
        
        {/* Modal de sugerencia de registro */}
        <RegistrationSuggestionModal
          isOpen={showRegistrationModal}
          onClose={handleCloseRegistrationModal}
          onRegister={handleRegisterFromModal}
          clientName={bookingData.clientName || 'Usuario'}
          appointmentCount={previousAppointments}
        />

        {/* Paso 3.5: Pago (Modal) */}
        {showPaymentStep && selectedService && professional && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Método de pago
                </h2>
                <button
                  onClick={() => setShowPaymentStep(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <PaymentStep
                professionalId={professional.id}
                servicePrice={selectedService.price}
                appointmentId={null}
                onPaymentMethodSelected={handlePaymentComplete}
                onBack={() => setShowPaymentStep(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookAppointment;